/**
 * Finding-text cleanup and resolution for quality_feedback descriptions,
 * shared by the item card and the Edit-item modal so the two surfaces can't
 * drift (ARC-221).
 *
 * Three display concerns live here:
 *  1. Column-letter prefixes ("N:", "AC:", "O:") and snake_case criterion
 *     prefixes ("avoid_negatives:") are analyzer bookkeeping and must be
 *     stripped from every message before it reaches the teacher — in the card,
 *     the edit modal, and the Unattributed sections of both.
 *  2. BCE's `_generate_feedback` emits an INTERNAL fallback description —
 *     f"{crit.value} ({column}) flagged" e.g. "one_correct_answer (O) flagged"
 *     — when it flagged a criterion but matched no issue text to its column
 *     letter. That is not user-facing copy; surfaces resolve the real finding
 *     from the cycle's `issues` array, and fall back to the established
 *     "Flagged, no details available." line only when no real finding exists.
 *  3. The real finding itself is often an UNPREFIXED issue line (e.g. the
 *     answer-key checker's "Answer key '...' does not match any option.") that
 *     BCE's column matching cannot attribute — it lives in `issues_found` and
 *     must be located by content/column shape before the internal fallback is
 *     shown.
 */

import { isDontTeachIssue } from "@/utils/socratesDontTeach";

/**
 * BCE's analyzer joins the several findings it produces for ONE criterion
 * into a single quality_feedback description with "; " (mcq_analyzer.py's
 * `'; '.join(matched)`). Each joined segment still carries its own
 * column-letter or snake_case prefix, e.g. "N: ...; N: ...". A join boundary
 * is therefore exactly "; " followed by another prefix; a single finding's
 * own "; " (no prefix after it, e.g. Avoid All or Nothing naming several
 * options, or Avoid Negatives naming a stem and an option) is not a boundary
 * and must stay whole.
 */
const FINDING_BOUNDARY_RE = /;\s*(?=[A-Z]{1,3}:|[A-Za-z]+_[A-Za-z0-9_]*:)/;

/** A message begins with a column-letter or snake_case criterion prefix. */
const LEADING_PREFIX_RE = /^\s*(?:[A-Z]{1,3}|[A-Za-z]+_[A-Za-z0-9_]*):\s*/;

/** A further column-prefixed segment joined into the same description
 *  ("N: ...; N: ..."). BCE joins several findings for one criterion into a
 *  single description with "; " (or " | "), and each carries its own prefix.
 *  Strip every one so no column letter reaches the teacher mid-message. */
const JOINED_PREFIX_RE = /(^|;\s*|\|\s*)(?:[A-Z]{1,3}|[A-Za-z]+_[A-Za-z0-9_]*):\s*/g;

/**
 * Criteria whose rewrite hints must not be shown on TestCraft: the analyzer's
 * service layer appends a SUGGESTION_BY_CRITERION line to quality_feedback for
 * all four of these criteria, and it is not part of the settled feedback
 * (ARC-358; ARC-379 extends the set to all four). Surfaces display only the
 * analyzer's own finding text, never the added hint.
 */
const HIDDEN_SUGGESTION_CRITERIA = new Set([
  "similar_option_length",
  "avoid_negatives",
  "present_problem_in_stem",
  "order_options_logically",
]);

export function isSuggestionHidden(criterion: string): boolean {
  return HIDDEN_SUGGESTION_CRITERIA.has((criterion ?? "").trim().toLowerCase());
}

/**
 * Strip a leading column/snake_case prefix (and any further prefixed segments
 * joined into the message) from a raw analyzer string. Pure text transform —
 * no capitalization.
 */
export function stripFeedbackPrefixes(text: string): string {
  let s = text.trim();
  const lead = s.match(LEADING_PREFIX_RE);
  if (lead) s = s.slice(lead[0].length);
  return s.replace(JOINED_PREFIX_RE, "$1");
}

/**
 * The display-ready form of an analyzer message for a finding block: column /
 * snake_case prefixes stripped, then the first real character capitalized.
 * Mirrors the old per-surface tidy helpers so card and modal agree.
 */
export function tidyFindingText(text: string): string {
  const trimmed = text.trim();
  const cleaned = stripFeedbackPrefixes(trimmed);
  if (cleaned.length === 0) return trimmed;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/**
 * Split a BCE-joined quality_feedback description into its separate findings.
 * The analyzer joins each matched issue string with "; " and every segment
 * keeps its own column-letter/snake_case prefix, so the boundaries split only
 * where a "; " is followed by another prefix. A lone "; " inside one finding
 * (Avoid All or Nothing's option list, Avoid Negatives' multiple locations)
 * has no prefix after it and is preserved — a single finding stays whole.
 * The returned segments keep their leading prefixes; callers run each through
 * tidyFindingText as usual.
 */
export function splitFindings(text: string): string[] {
  const trimmed = (text ?? "").trim();
  if (trimmed.length === 0) return [];
  return trimmed
    .split(FINDING_BOUNDARY_RE)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** One finding bucket holding the per-criterion bullet lists plus the
 *  parallel suggestion lines (aligned by index). */
export interface FindingBucket {
  failed: Record<string, string[]>;
  judgment: Record<string, string[]>;
  failedSuggestions: Record<string, string[]>;
  judgmentSuggestions: Record<string, string[]>;
  /** Entries whose criterion can't be attributed to a flagged key —
   *  rendered in the Unattributed section (ARC-212). */
  unattributed: Array<{
    criterion: string;
    severity: string;
    description: string;
    suggestion: string;
  }>;
  /** Criteria already served by a quality_feedback description, so the
   *  raw-issue fallback never double-renders the same finding. */
  served: Set<string>;
}

/**
 * Route a cycle's quality_feedback entries and raw issues into per-criterion
 * finding buckets, shared by the item card and the Edit modal so the two
 * surfaces can't drift (ARC-354).
 *
 * Every finding carries its OWN mark: a finding from a severity=advisory
 * entry is a judgment call (blue, Needs your judgment); every other finding
 * is a genuine failure (red, Issues found). A criterion can carry one
 * advisory AND one failed entry, and then appears under both headings below —
 * its findings never mix. Finding text is displayed as the analyzer wrote it
 * (Waggle never re-sorts answer options, so its option letters already match
 * the display order — no remapping is applied). Each separate finding (a
 * "; "-joined description may hold several) becomes its own bullet via
 * splitFindings.
 */
export function bucketSocratesFindings(opts: {
  flags: Record<string, number>;
  feedback: ReadonlyArray<{
    criterion?: string;
    severity?: string;
    description?: string;
    suggestion?: string | null;
  }>;
  /** Raw cycle issues (unfiltered) — the resolution source for the
   *  analyzer's internal "<criterion> (<column>) flagged" fallback. */
  rawIssues: string[];
  /** Raw issues already marker-filtered — the bucketing source for
   *  validator lines carrying a snake_case criterion prefix. */
  filteredIssues: string[];
  /** The criterion-level judgment state (judgmentCriterionKeys). Used ONLY
   *  to route raw-issue findings (which carry no severity of their own) and
   *  to keep a flagged-but-detail-less criterion on its pre-existing heading. */
  judgmentKeys: Set<string>;
}): FindingBucket {
  const failed: Record<string, string[]> = {};
  const judgment: Record<string, string[]> = {};
  const failedSuggestions: Record<string, string[]> = {};
  const judgmentSuggestions: Record<string, string[]> = {};
  const unattributed: FindingBucket["unattributed"] = [];
  const served = new Set<string>();

  const isAdvisory = (severity: string | undefined): boolean =>
    (severity ?? "").trim().toLowerCase() === "advisory";

  for (const fb of opts.feedback) {
    const crit = (fb.criterion ?? "").trim().toLowerCase();
    const rawDesc = (fb.description ?? "").trim();
    const suggestion = (fb.suggestion ?? "").trim();
    // The analyzer's internal "<criterion> (<column>) flagged" fallback is
    // bookkeeping, never user copy: resolve the real finding from the cycle's
    // issues when one appears (ARC-221 faults 2 & 3).
    const internalFallback = parseInternalFallback(rawDesc);
    let source = "";
    if (internalFallback) {
      source = findRealFindingForCriterion(
        opts.rawIssues,
        internalFallback.criterion,
        internalFallback.column,
      ) ?? "";
    } else {
      source = rawDesc;
    }
    if (source.trim().length === 0) continue;
    const tidied = splitFindings(source)
      .map(tidyFindingText)
      .filter((f) => f.length > 0);
    if (tidied.length === 0) continue;
    // Don't Teach's raw finding must never surface as a detached line or
    // inside Unattributed — the blue guidance block already carries it. Scope
    // by message text (raw and tidied) so a criterion=unknown historical row
    // can't leak the raw "Z:" / "AC:" wording (ARC-12 / ARC-219).
    if (isDontTeachIssue(rawDesc) || tidied.some(isDontTeachIssue)) continue;
    const attributed =
      crit.length > 0 && crit !== "unknown" && crit in opts.flags && opts.flags[crit] === 1;
    if (!attributed) {
      unattributed.push({
        criterion: crit,
        severity: fb.severity ?? "",
        description: tidied.join(" "),
        suggestion,
      });
      continue;
    }
    const toJudgment = isAdvisory(fb.severity);
    const bucket = toJudgment ? judgment : failed;
    const sugBucket = toJudgment ? judgmentSuggestions : failedSuggestions;
    (bucket[crit] = bucket[crit] ?? []).push(...tidied);
    // One suggestion line per feedback entry, attached to the entry's first
    // bullet; a joined description that splits into several bullets must not
    // repeat the same rewrite guidance per bullet.
    (sugBucket[crit] = sugBucket[crit] ?? []).push(suggestion, ...tidied.slice(1).map(() => ""));
    served.add(crit);
  }

  for (const raw of opts.filteredIssues) {
    // Don't Teach messages are handled by the blue guidance block, never by
    // the raw bucketing — regardless of whether this cycle flags the
    // criterion.
    if (isDontTeachIssue(raw)) continue;
    const m = raw.match(/^\s*([a-z][a-z0-9_]+):\s*/i);
    const key = m && m[1].includes("_") ? m[1].toLowerCase() : null;
    if (key && served.has(key)) continue;
    if (key && key in opts.flags && opts.flags[key] === 1) {
      const body = tidyFindingText(raw.slice(m![0].length));
      // A raw issue carries no severity; the criterion's judgment state
      // routes it (ARC-240 parity with the old criterion-level lists).
      const toJudgment = opts.judgmentKeys.has(key);
      const bucket = toJudgment ? judgment : failed;
      const sugBucket = toJudgment ? judgmentSuggestions : failedSuggestions;
      (bucket[key] = bucket[key] ?? []).push(body);
      (sugBucket[key] = sugBucket[key] ?? []).push("");
    }
    // Unattributable (no snake_case prefix, or the criterion isn't flagged):
    // dropped. See ARC-219 — no detached block.
  }

  return { failed, judgment, failedSuggestions, judgmentSuggestions, unattributed, served };
}

/** The internal fallback BCE emits when it can match no issue text to a
 *  flagged criterion's column letter: f"{crit.value} ({column}) flagged",
 *  e.g. "one_correct_answer (O) flagged" — analyzer bookkeeping, never meant
 *  to be shown to the teacher. */
const INTERNAL_FALLBACK_RE = /^([a-z][a-z0-9_]*)\s*\(\s*([A-Z]{1,3})\s*\)\s*flagged$/i;

export interface InternalFallback {
  /** The snake_case criterion key embedded in the string. */
  criterion: string;
  /** The criterion's BCE column letter embedded in the string. */
  column: string;
}

/**
 * True when a quality_feedback description is the analyzer's internal
 * "<criterion> (<column>) flagged" fallback — i.e. the criterion fired but no
 * issue text was matched to its column. Surfaces must resolve the real finding
 * from the cycle's issues instead of printing the key to the user.
 */
export function parseInternalFallback(text: string): InternalFallback | null {
  const m = text.trim().match(INTERNAL_FALLBACK_RE);
  return m
    ? { criterion: m[1].toLowerCase(), column: m[2].toUpperCase() }
    : null;
}

/** Column-letter-prefixed issue line for a criterion's column ("O:", or a
 *  sub-variant like "O_conceptual:"). */
const columnPrefixed = (column: string): RegExp =>
  new RegExp(`^\\s*${column}(?:_[A-Za-z0-9]+)?:`, "i");

/**
 * Content signatures for findings that BCE emits WITHOUT a column-letter
 * prefix, so its `issues_for_column` attribution cannot match them and the
 * internal "<criterion> (<column>) flagged" fallback is produced instead.
 * The unprefixed issue text itself is the authoritative record of the finding
 * and must be located and rendered (ARC-221 fault 3). Keyed by criterion.
 */
const UNPREFIXED_FINDING_SHAPES: Record<string, RegExp> = {
  // The Rust answer-key checker reports a key that matches no option as
  // "Answer key '...' does not match any option. Valid options: ..." with NO
  // leading "O:" — the concrete case behind fault 3 (pre-PR #117 the item
  // rendered "Answer does not match any option" under One Correct Answer).
  one_correct_answer: /does not match any option/i,
};

/** Whether an issue line's content matches a criterion's expected finding
 *  shape (used when the line has no column-letter prefix by which to
 *  attribute it). */
const matchesCriterionShape = (criterion: string, text: string): boolean => {
  const shape = UNPREFIXED_FINDING_SHAPES[criterion];
  return shape ? shape.test(text) : false;
};

/**
 * Locate the real, user-facing finding for a flagged criterion inside the
 * cycle's raw `issues` array. Returns the first issue line that either starts
 * with the criterion's column letter (its normal emitted form) or whose
 * content matches the criterion's expected finding shape (unprefixed forms
 * BCE's column matching can't attribute). The line is returned verbatim —
 * callers strip prefixes and remap option letters for display. Returns null
 * when no issue describes the criterion's concern.
 */
export function findRealFindingForCriterion(
  issues: string[],
  criterion: string,
  column: string,
): string | null {
  for (const raw of issues) {
    const s = raw.trim();
    if (s.length === 0) continue;
    if (columnPrefixed(column).test(s)) return s;
    if (matchesCriterionShape(criterion, s)) return s;
  }
  return null;
}